import {
  Curve,
  LineSegment,
  Obstacle,
  Point,
  TrajectoryGroup,
} from '../types/sharedTypes';

export class CollisionDetectionService {
  public getLineSegmentColissionPoint(
    firstLine: LineSegment,
    secondLine: LineSegment
  ): Point | null {
    // Check if none of the line segments are of length 0
    if (
      (firstLine.start.x === firstLine.end.x &&
        firstLine.start.y === firstLine.end.y) ||
      (secondLine.start.x === secondLine.end.x &&
        secondLine.start.y === secondLine.end.y)
    ) {
      return null;
    }

    const denominator =
      (secondLine.end.y - secondLine.start.y) *
      (firstLine.end.x - firstLine.start.x) -
      (secondLine.end.x - secondLine.start.x) *
      (firstLine.end.y - firstLine.start.y);

    // Check if the lines are parallel
    if (denominator === 0) {
      return null;
    }

    const ua =
      ((secondLine.end.x - secondLine.start.x) *
        (firstLine.start.y - secondLine.start.y) -
        (secondLine.end.y - secondLine.start.y) *
        (firstLine.start.x - secondLine.start.x)) /
      denominator;
    const ub =
      ((firstLine.end.x - firstLine.start.x) *
        (firstLine.start.y - secondLine.start.y) -
        (firstLine.end.y - firstLine.start.y) *
        (firstLine.start.x - secondLine.start.x)) /
      denominator;

    // Check if the the intersection is outside the segments
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
      return null;
    }

    // Return a object with the x and y coordinates of the intersection
    const intersectionX =
      firstLine.start.x + ua * (firstLine.end.x - firstLine.start.x);
    const intersectionY =
      firstLine.start.y + ua * (firstLine.end.y - firstLine.start.y);

    return { x: intersectionX, y: intersectionY };
  }

  /**
   * Takes a group of trajectories (main, min-footprint, max-footprint) and cuts it off at given obstacles.
   * The trajectoryGroup parameter is modified directly.
   * Returns true if a cut-off occured, otherwise false.
   */
  public cutTrajectoryAtObstacles({
    trajectoryGroup,
    obstacles,
  }: {
    trajectoryGroup: TrajectoryGroup;
    obstacles: Array<Obstacle>;
  }): boolean {
    const obstacleLineSegments = this.getLineSegmentsFromObstacles(obstacles);

    // i starting at 1 is intentional, because two points are required to form a line-segment
    for (let i = 1; i < trajectoryGroup.mainTrajectory.length; i++) {

      let didMainCollide = false;
      let didMinCollide = false;
      let didMaxCollide = false;

      const obstacleCollisionPoints: Array<Point> = [];

      if (
        trajectoryGroup.mainTrajectory[i] &&
        trajectoryGroup.mainTrajectory[i - 1]
      ) {

        // Line-Segment between previous trajectory point and current one.
        const trajectoryLineSegment: LineSegment = {
          start: {
            x: trajectoryGroup.mainTrajectory[i - 1].x,
            y: trajectoryGroup.mainTrajectory[i - 1].y,
          },
          end: {
            x: trajectoryGroup.mainTrajectory[i].x,
            y: trajectoryGroup.mainTrajectory[i].y,
          },
        };

        const leftmostCollisionPoint =
          this.getLeftmostCollisionBetweenTrajectorySegmentAndObstacleSegments(
            trajectoryLineSegment,
            obstacleLineSegments
          );

        if (leftmostCollisionPoint) {
          // Break mainTrajectory at collision point
          trajectoryGroup.mainTrajectory[i] = leftmostCollisionPoint;
          trajectoryGroup.mainTrajectory.splice(i + 1);

          didMainCollide = true;
          obstacleCollisionPoints.push(leftmostCollisionPoint);
        }
      }

      if (
        trajectoryGroup.minFootprintTrajectory[i] &&
        trajectoryGroup.minFootprintTrajectory[i - 1]
      ) {
        // Line-Segment between previous trajectory point and current one.
        const trajectoryLineSegment: LineSegment = {
          start: {
            x: trajectoryGroup.minFootprintTrajectory[i - 1].x,
            y: trajectoryGroup.minFootprintTrajectory[i - 1].y,
          },
          end: {
            x: trajectoryGroup.minFootprintTrajectory[i].x,
            y: trajectoryGroup.minFootprintTrajectory[i].y,
          },
        };

        const leftmostCollisionPoint =
          this.getLeftmostCollisionBetweenTrajectorySegmentAndObstacleSegments(
            trajectoryLineSegment,
            obstacleLineSegments
          );

        if (leftmostCollisionPoint) {
          // Break trajectoryGroup.minFootprintTrajectory at collision point
          trajectoryGroup.minFootprintTrajectory[i] = leftmostCollisionPoint;
          trajectoryGroup.minFootprintTrajectory.splice(i + 1);

          didMinCollide = true;
          obstacleCollisionPoints.push(leftmostCollisionPoint);
        }
      }

      if (
        trajectoryGroup.maxFootprintTrajectory[i] &&
        trajectoryGroup.maxFootprintTrajectory[i - 1]
      ) {
        // Line-Segment between previous trajectory point and current one.
        const trajectoryLineSegment: LineSegment = {
          start: {
            x: trajectoryGroup.maxFootprintTrajectory[i - 1].x,
            y: trajectoryGroup.maxFootprintTrajectory[i - 1].y,
          },
          end: {
            x: trajectoryGroup.maxFootprintTrajectory[i].x,
            y: trajectoryGroup.maxFootprintTrajectory[i].y,
          },
        };

        const leftmostCollisionPoint =
          this.getLeftmostCollisionBetweenTrajectorySegmentAndObstacleSegments(
            trajectoryLineSegment,
            obstacleLineSegments
          );

        if (leftmostCollisionPoint) {
          // Break trajectoryGroup.maxFootprintTrajectory at collision point
          trajectoryGroup.maxFootprintTrajectory[i] = leftmostCollisionPoint;
          trajectoryGroup.maxFootprintTrajectory.splice(i + 1);

          didMaxCollide = true;
          obstacleCollisionPoints.push(leftmostCollisionPoint);
        }
      }

      if (didMainCollide || didMinCollide || didMaxCollide) {

        if (!didMainCollide) {
          this.cutOffTrajectoryAtWidth({
            trajectory: trajectoryGroup.mainTrajectory,
            dontCutBeforeIndex: i,
            obstacleCollisionPoints,
          });
          this.cutOffTrajectoryAtHeight({
            trajectory: trajectoryGroup.mainTrajectory,
            dontCutBeforeIndex: i,
            obstacleCollisionPoints,
          });
        }

        if (!didMinCollide) {
          this.cutOffTrajectoryAtWidth({
            trajectory: trajectoryGroup.minFootprintTrajectory,
            dontCutBeforeIndex: i,
            obstacleCollisionPoints,
          });
          this.cutOffTrajectoryAtHeight({
            trajectory: trajectoryGroup.minFootprintTrajectory,
            dontCutBeforeIndex: i,
            obstacleCollisionPoints,
          });
        }

        if (!didMaxCollide) {
          this.cutOffTrajectoryAtWidth({
            trajectory: trajectoryGroup.maxFootprintTrajectory,
            dontCutBeforeIndex: i,
            obstacleCollisionPoints,
          });
          this.cutOffTrajectoryAtHeight({
            trajectory: trajectoryGroup.maxFootprintTrajectory,
            dontCutBeforeIndex: i,
            obstacleCollisionPoints,
          });
        }

        return true;

      }
    }

    return false;
  }

  protected cutOffTrajectoryAtHeight({
    trajectory,
    dontCutBeforeIndex,
    obstacleCollisionPoints,
  }: {
    trajectory: Curve,
    dontCutBeforeIndex: number,
    obstacleCollisionPoints: Array<Point>
  }): void {

    for (let i = dontCutBeforeIndex; i < trajectory.length; i++) {
      const previousPoint = trajectory[i - 1];
      const currentPoint = trajectory[i];

      if (!previousPoint || !currentPoint) {
        continue;
      }

      const collidingObstacle = obstacleCollisionPoints.find(obstacleCollisionPoint => (
        (
          previousPoint.y < obstacleCollisionPoint.y
          && currentPoint.y > obstacleCollisionPoint.y
        )
        ||
        (
          previousPoint.y > obstacleCollisionPoint.y
          && currentPoint.y < obstacleCollisionPoint.y
        )
      ));

      if (!collidingObstacle) {
        continue;
      }

      const cutOffPoint = this.getLineSegmentColissionPoint(
        {
          start: previousPoint,
          end: currentPoint,
        },
        {
          start: { x: 0, y: collidingObstacle.y },
          end: { x: 10000, y: collidingObstacle.y },
        },
      );

      if (!cutOffPoint) {
        continue;
      }

      trajectory[i] = cutOffPoint;
      trajectory.splice(i + 1);
      return;
    }
  }

  protected cutOffTrajectoryAtWidth({
    trajectory,
    dontCutBeforeIndex,
    obstacleCollisionPoints,
  }: {
    trajectory: Curve,
    dontCutBeforeIndex: number,
    obstacleCollisionPoints: Array<Point>
  }): void {

    for (let i = dontCutBeforeIndex; i < trajectory.length; i++) {
      const previousPoint = trajectory[i - 1];
      const currentPoint = trajectory[i];

      if (!previousPoint || !currentPoint) {
        continue;
      }

      const collidingObstacle = obstacleCollisionPoints.find(obstacleCollisionPoint => (
        previousPoint.x < obstacleCollisionPoint.x
        && currentPoint.x > obstacleCollisionPoint.x
      ));

      if (!collidingObstacle) {
        continue;
      }

      const cutOffPoint = this.getLineSegmentColissionPoint(
        {
          start: previousPoint,
          end: currentPoint,
        },
        {
          start: { x: collidingObstacle.x, y: 0 },
          end: { x: collidingObstacle.x, y: 10000 },
        },
      );

      if (!cutOffPoint) {
        continue;
      }

      trajectory[i] = cutOffPoint;
      trajectory.splice(i + 1);
      return;
    }
  }

  protected getLeftmostCollisionBetweenTrajectorySegmentAndObstacleSegments(
    trajectoryLineSegment: LineSegment,
    obstacleLineSegments: Array<LineSegment>
  ): Point | null {
    // Detect all collision points between trajectory-line-segment and all obstacle-line-segments.
    // May result in multiple collision points, for example first on obstacle-left then obstacle-right.
    // Sort them ascending by their x-coordinate.
    // Take the first (left-most) one.

    return (
      obstacleLineSegments
        .map((obstacleLineSegment) =>
          this.getLineSegmentColissionPoint(
            trajectoryLineSegment,
            obstacleLineSegment
          )
        )
        .filter((point) => point !== null)
        .sort((a, b) => (a?.x ?? 0) - (b?.x ?? 0))
        .at(0) ?? null
    );
  }

  protected getLineSegmentsFromObstacles(
    obstacles: Array<Obstacle>
  ): Array<LineSegment> {
    return obstacles.reduce<Array<LineSegment>>((lineSegments, obstacle) => {
      return [...lineSegments, ...this.getLineSegmentsFromObstacle(obstacle)];
    }, [] as Array<LineSegment>);
  }

  /**
   * Using 100.000 points for obstacle.top if not defined is needed here.
   * Since we dont know the obstacle.top, it is undefined by default for roof obstacles.
   * It scales dynamically with the height of the curve within the chart.
   * To avoid a "none detection" of our roof-obstacles, we using 100.000 as a workaround for the detection functions.
   */
  protected getLineSegmentsFromObstacle(
    obstacle: Obstacle
  ): Array<LineSegment> {
    const top: LineSegment = {
      start: { x: obstacle.left, y: obstacle.top ?? 100000 },
      end: { x: obstacle.right, y: obstacle.top ?? 100000 },
    };

    const bottom: LineSegment = {
      start: { x: obstacle.left, y: obstacle.bottom },
      end: { x: obstacle.right, y: obstacle.bottom },
    };

    const left: LineSegment = {
      start: { x: obstacle.left, y: obstacle.top ?? 100000 },
      end: { x: obstacle.left, y: obstacle.bottom },
    };

    const right: LineSegment = {
      start: { x: obstacle.right, y: obstacle.top ?? 100000 },
      end: { x: obstacle.right, y: obstacle.bottom },
    };

    return [top, bottom, left, right];
  }
}
